import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Rocket, Save } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { pageContentSchema, type PageContent, type SiteBlock, type SiteBlockType } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api, errorMessage, HttpError } from '@/lib/api';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/utils';
import { BlockForm } from './block-form';
import { BlocksPanel } from './blocks-panel';
import { HistoryTab } from './history-tab';
import { PreviewPane } from './preview-pane';
import { SeoTab } from './seo-tab';
import { BLOCK_LABELS, newBlock, pagesKey, type PageDetail, type PageSummary } from './types';
import { AddBlockMenu } from './blocks-panel';

interface Props {
  detail: PageDetail;
  pages: PageSummary[];
  orgSlug: string | undefined;
  onReload: () => void;
  onDirtyChange: (d: boolean) => void;
}

function Editor({ detail, pages, orgSlug, onReload, onDirtyChange }: Props) {
  const qc = useQueryClient();
  const { can } = useSession();
  const [content, setContent] = useState<PageContent>(detail.draft);
  const [version, setVersion] = useState(detail.draftVersion);
  const [dirty, setDirty] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(detail.draft.blocks[0]?.id ?? null);
  const [view, setView] = useState<'edit' | 'preview'>('edit');
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('blocos');

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  const edit = useCallback((next: PageContent) => {
    setContent(next);
    setDirty(true);
  }, []);
  const setBlocks = (blocks: SiteBlock[]) => edit({ ...content, blocks });

  const selected = content.blocks.find((b) => b.id === selectedId) ?? null;

  const save = useCallback(async (): Promise<boolean> => {
    const parsed = pageContentSchema.safeParse(content);
    if (!parsed.success) {
      const issue = parsed.error.issues[0]!;
      const idx = issue.path[0] === 'blocks' ? Number(issue.path[1]) : -1;
      const bad = content.blocks[idx];
      if (bad) {
        setSelectedId(bad.id);
        setTab('blocos');
        setView('edit');
      }
      toast.error(`Corrija antes de salvar${bad ? ` (${BLOCK_LABELS[bad.type]})` : ''}: ${issue.message === 'URL não permitida' ? 'endereço do link não permitido' : issue.message}`);
      return false;
    }
    setBusy(true);
    try {
      const r = await api.put<{ draftVersion: number }>(`/site/pages/${detail.id}/draft`, { content: parsed.data, baseVersion: version });
      setVersion(r.draftVersion);
      setDirty(false);
      qc.invalidateQueries({ queryKey: pagesKey });
      toast.success('Rascunho salvo');
      return true;
    } catch (e) {
      if (e instanceof HttpError && e.code === 'draft_conflict') {
        toast.error('Outra pessoa alterou esta página. Recarregamos a versão mais recente; refaça suas edições se precisar.');
        onReload();
      } else if (e instanceof HttpError && e.status === 422) toast.error(e.body.issues?.[0]?.message ?? e.message);
      else toast.error(errorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  }, [content, version, detail.id, qc, onReload]);

  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void saveRef.current();
      }
    };
    const onUnload = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, []);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  async function publish() {
    if (dirty && !(await save())) throw new Error('save');
    try {
      const r = await api.post<{ versionNumber: number; slug: string }>(`/site/pages/${detail.id}/publish`);
      await qc.invalidateQueries({ queryKey: pagesKey });
      qc.invalidateQueries({ queryKey: ['site', 'page', detail.id] });
      const path = `/b/${orgSlug ?? ''}${r.slug ? `/${r.slug}` : ''}`;
      toast.success('Site publicado', { description: `Versão ${r.versionNumber} no ar.`, action: { label: 'Abrir site', onClick: () => window.open(path, '_blank', 'noopener') } });
    } catch (e) {
      toast.error(e instanceof HttpError && e.status === 403 ? 'Você não tem permissão para publicar.' : errorMessage(e));
      throw e;
    }
  }

  const livePath = `/b/${orgSlug ?? ''}${detail.slug ? `/${detail.slug}` : ''}`;

  return (
    <div className="grid min-w-0 gap-4">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card p-3">
        <div className="mr-auto min-w-0">
          <p className="truncate font-medium">{detail.title}</p>
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {dirty ? <span className="font-medium text-warning">Alterações não salvas</span> : 'Rascunho salvo'}
            {detail.published ? ` · No ar: versão ${detail.publishedVersionNumber}` : ' · Ainda não publicada'}
          </p>
        </div>
        {detail.published && (
          <Button asChild variant="ghost" size="sm">
            <a href={livePath} target="_blank" rel="noopener noreferrer"><ExternalLink aria-hidden /> Ver no ar</a>
          </Button>
        )}
        <Button type="button" variant="outline" size="sm" onClick={() => void save()} disabled={busy || !dirty} title="Ctrl+S">
          <Save aria-hidden /> Salvar rascunho
        </Button>
        {can('site.publish') && (
          <ConfirmDialog
            destructive={false}
            trigger={<Button type="button" size="sm" disabled={busy}><Rocket aria-hidden /> Publicar</Button>}
            title="Publicar esta página?"
            description={dirty ? 'As alterações não salvas serão salvas e a página nova vai ao ar para todos os visitantes.' : 'O rascunho atual vai ao ar para todos os visitantes e cria uma nova versão no histórico.'}
            confirmLabel="Publicar agora"
            onConfirm={publish}
          />
        )}
      </div>

      <div role="group" aria-label="Alternar painel" className="inline-flex w-fit rounded-md border p-0.5 2xl:hidden">
        {(['edit', 'preview'] as const).map((v) => (
          <Button key={v} type="button" size="sm" variant={view === v ? 'secondary' : 'ghost'} aria-pressed={view === v} onClick={() => setView(v)}>
            {v === 'edit' ? 'Editar' : 'Pré-visualizar'}
          </Button>
        ))}
      </div>

      <div className="grid min-w-0 items-start gap-4 2xl:grid-cols-[24rem_minmax(0,1fr)]">
        <div className={cn('min-w-0', view === 'preview' && 'hidden 2xl:block')}>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="blocos">Blocos</TabsTrigger>
              <TabsTrigger value="seo">SEO</TabsTrigger>
              <TabsTrigger value="historico">Histórico</TabsTrigger>
            </TabsList>
            <TabsContent value="blocos" className="grid gap-5">
              <BlocksPanel
                blocks={content.blocks}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onChange={setBlocks}
                onAdd={(t: SiteBlockType) => {
                  const b = newBlock(t);
                  setBlocks([...content.blocks, b]);
                  setSelectedId(b.id);
                }}
              />
              {selected && (
                <section aria-label="Propriedades do bloco" className="rounded-lg border bg-card p-4">
                  <h3 className="mb-4 text-sm font-semibold">{BLOCK_LABELS[selected.type]}</h3>
                  <BlockForm
                    key={selected.id}
                    block={selected}
                    pages={pages}
                    onChange={(b) => {
                      setContent((c) => ({ ...c, blocks: c.blocks.map((x) => (x.id === b.id ? b : x)) }));
                      setDirty(true);
                    }}
                  />
                </section>
              )}
              {content.blocks.length === 0 && (
                <div className="flex justify-center">
                  <AddBlockMenu onAdd={(t) => { const b = newBlock(t); setBlocks([b]); setSelectedId(b.id); }} />
                </div>
              )}
            </TabsContent>
            <TabsContent value="seo">
              <SeoTab seo={content.seo} onChange={(seo) => edit({ ...content, seo })} />
            </TabsContent>
            <TabsContent value="historico">
              <HistoryTab
                pageId={detail.id}
                versions={detail.versions}
                dirty={dirty}
                onRestored={() => {
                  setDirty(false);
                  qc.invalidateQueries({ queryKey: pagesKey });
                  onReload();
                }}
              />
            </TabsContent>
          </Tabs>
        </div>
        <div className={cn('min-w-0 2xl:sticky 2xl:top-4', view === 'edit' && 'hidden 2xl:block')}>
          <PreviewPane pageId={detail.id} draftVersion={version} dirty={dirty} onSave={() => void save()} />
        </div>
      </div>
    </div>
  );
}

/** Carrega o detalhe da página e remonta o editor ao recarregar (conflito, restauração, troca de página). */
export function PageEditor({ pageId, pages, orgSlug, onDirtyChange }: { pageId: string; pages: PageSummary[]; orgSlug: string | undefined; onDirtyChange: (d: boolean) => void }) {
  const qc = useQueryClient();
  const [epoch, setEpoch] = useState(0);
  const q = useQuery({
    queryKey: ['site', 'page', pageId],
    queryFn: () => api.get<PageDetail>(`/site/pages/${pageId}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });
  if (q.isPending) return <LoadingState label="Abrindo página…" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  return (
    <Editor
      key={`${pageId}:${epoch}`}
      detail={q.data}
      pages={pages}
      orgSlug={orgSlug}
      onDirtyChange={onDirtyChange}
      onReload={async () => {
        await qc.invalidateQueries({ queryKey: ['site', 'page', pageId] });
        setEpoch((e) => e + 1);
      }}
    />
  );
}
